// Platform Billing console (admin-portal): overview KPIs, per-tenant billing
// (subscription config + invoices + lifecycle actions + manual payment), reports,
// and global billing settings. Real data from billing-service.
import { fmtDate, fmtDateTime } from '../../lib/format';
import React, { useEffect, useState } from 'react';
import {
  Card, Table, Tag, Row, Col, Statistic, Tabs, Typography, Space, Button, Drawer, Form,
  InputNumber, Select, Switch, Modal, Input, message, Descriptions, Popconfirm,
} from 'antd';
import { DollarOutlined, ReloadOutlined } from '@ant-design/icons';
import * as api from '../../services/billingService';

const { Title, Text } = Typography;

const money = (v: any, cur = 'TZS') =>
  `${cur} ${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const statusColor: Record<string, string> = {
  active: 'green', pending_setup: 'default', past_due: 'gold', suspended: 'red',
  contract_ended: 'purple', deleted: 'default', cancelled: 'default',
};
const invColor: Record<string, string> = {
  paid: 'green', sent: 'blue', issued: 'cyan', partially_paid: 'gold',
  overdue: 'red', void: 'default', cancelled: 'default', draft: 'default',
};

const Overview: React.FC = () => {
  const [o, setO] = useState<api.BillingOverview | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.getOverview().then(setO).catch(() => undefined).finally(() => setLoading(false));
  }, []);
  return (
    <Row gutter={[16, 16]}>
      {[
        ['Monthly Recurring Revenue', money(o?.mrr)],
        ['Annual Recurring Revenue', money(o?.arr)],
        ['Total Outstanding', money(o?.totalOutstanding)],
        ['Past Due', money(o?.pastDueAmount)],
        ['Paid This Month', money(o?.paidThisMonth)],
        ['Failed Payments', o?.failedPayments ?? 0],
        ['Customers Past Due', o?.customersPastDue ?? 0],
        ['Customers Suspended', o?.customersSuspended ?? 0],
      ].map(([label, val]) => (
        <Col span={6} key={label as string}>
          <Card loading={loading}><Statistic title={label as string} value={val as any} /></Card>
        </Col>
      ))}
    </Row>
  );
};

const TenantDrawer: React.FC<{ tenantId: string | null; onClose: () => void }> = ({ tenantId, onClose }) => {
  const [data, setData] = useState<Awaited<ReturnType<typeof api.getTenant>> | null>(null);
  const [loading, setLoading] = useState(false);
  const [subForm] = Form.useForm();
  const [accForm] = Form.useForm();
  const [payOpen, setPayOpen] = useState(false);
  const [payForm] = Form.useForm();
  const [creditOpen, setCreditOpen] = useState(false);
  const [creditForm] = Form.useForm();

  const load = () => {
    if (!tenantId) return;
    setLoading(true);
    api.getTenant(tenantId).then((d) => {
      setData(d);
      subForm.setFieldsValue({
        amount: Number(d.subscription?.amount || 0),
        billingCycle: d.subscription?.billingCycle || 'monthly',
        currency: d.subscription?.currency || d.account.currency,
        defaultPaymentProvider: d.subscription?.defaultPaymentProvider || 'AZAMPAY',
        allowManualPayment: d.subscription?.allowManualPayment ?? true,
        allowAzampay: d.subscription?.allowAzampay ?? true,
        allowStripe: d.subscription?.allowStripe ?? false,
      });
      accForm.setFieldsValue({
        currency: d.account.currency,
        tenantCountry: d.account.tenantCountry || undefined,
        billingEmail: d.account.billingEmail || undefined,
        gracePeriodDays: d.account.gracePeriodDays,
        paymentTermsDays: d.account.paymentTermsDays,
      });
    }).catch((e) => message.error(e?.response?.data?.message || 'Could not load tenant.'))
      .finally(() => setLoading(false));
  };
  useEffect(load, [tenantId]);

  const act = async (fn: () => Promise<any>, ok: string) => {
    try { await fn(); message.success(ok); load(); } catch (e: any) { message.error(e?.response?.data?.message || 'Action failed.'); }
  };
  const downloadPdf = async (invoiceId: string) => {
    try {
      const { url } = await api.getInvoicePdf(invoiceId);
      if (url) window.open(url, '_blank', 'noopener');
      else message.error('PDF not available.');
    } catch (e: any) { message.error(e?.response?.data?.message || 'Could not open the invoice PDF.'); }
  };
  const saveSub = async () => {
    const v = await subForm.validateFields();
    act(() => api.upsertSubscription(tenantId!, v), 'Subscription saved');
  };
  const saveAcc = async () => {
    const v = await accForm.validateFields();
    act(() => api.updateAccount(tenantId!, v), 'Account updated');
  };
  const submitPay = async () => {
    const v = await payForm.validateFields();
    await act(() => api.recordManualPayment({ tenantId, ...v }), 'Payment recorded');
    setPayOpen(false); payForm.resetFields();
  };
  const submitCredit = async () => {
    const v = await creditForm.validateFields();
    await act(() => api.adjustCredit(tenantId!, v), 'Credit adjusted');
    setCreditOpen(false); creditForm.resetFields();
  };

  const a = data?.account;
  return (
    <Drawer title={a?.tenantNameSnapshot || 'Tenant billing'} width={720} open={!!tenantId} onClose={onClose} loading={loading}>
      {a && (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Descriptions size="small" column={2} bordered>
            <Descriptions.Item label="Status"><Tag color={statusColor[a.accountStatus]}>{a.accountStatus}</Tag></Descriptions.Item>
            <Descriptions.Item label="Balance">{money(a.currentBalance, a.currency)}</Descriptions.Item>
            <Descriptions.Item label="Credit balance">{money(a.creditBalance, a.currency)}</Descriptions.Item>
          </Descriptions>

          <Card size="small" title="Account">
            <Form form={accForm} layout="vertical">
              <Row gutter={12}>
                <Col span={8}><Form.Item name="currency" label="Currency"><Select options={[{ value: 'TZS' }, { value: 'USD' }, { value: 'KES' }, { value: 'RWF' }]} /></Form.Item></Col>
                <Col span={8}><Form.Item name="tenantCountry" label="Country"><Select allowClear options={[{ value: 'TZ', label: 'Tanzania (TZ)' }, { value: 'KE', label: 'Kenya (KE)' }, { value: 'RW', label: 'Rwanda (RW)' }]} /></Form.Item></Col>
                <Col span={8}><Form.Item name="billingEmail" label="Billing email"><Input placeholder="billing@company.com" /></Form.Item></Col>
              </Row>
              <Row gutter={12}>
                <Col span={8}><Form.Item name="gracePeriodDays" label="Grace days"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>
                <Col span={8}><Form.Item name="paymentTermsDays" label="Terms days"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>
              </Row>
              <Button type="primary" onClick={saveAcc}>Save account</Button>
            </Form>
          </Card>

          <Card size="small" title="Subscription">
            <Form form={subForm} layout="vertical">
              <Row gutter={12}>
                <Col span={8}><Form.Item name="amount" label="Amount" rules={[{ required: true }]}><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>
                <Col span={8}><Form.Item name="billingCycle" label="Cycle"><Select options={[{ value: 'monthly' }, { value: 'yearly' }]} /></Form.Item></Col>
                <Col span={8}><Form.Item name="currency" label="Currency"><Select options={[{ value: 'TZS' }, { value: 'USD' }]} /></Form.Item></Col>
              </Row>
              <Row gutter={12}>
                <Col span={8}><Form.Item name="defaultPaymentProvider" label="Provider"><Select options={[{ value: 'AZAMPAY' }, { value: 'STRIPE' }, { value: 'MANUAL' }]} /></Form.Item></Col>
                <Col span={5}><Form.Item name="allowAzampay" label="AzamPay" valuePropName="checked"><Switch /></Form.Item></Col>
                <Col span={5}><Form.Item name="allowStripe" label="Stripe" valuePropName="checked"><Switch /></Form.Item></Col>
                <Col span={6}><Form.Item name="allowManualPayment" label="Manual" valuePropName="checked"><Switch /></Form.Item></Col>
              </Row>
              <Button type="primary" onClick={saveSub}>Save subscription</Button>
            </Form>
          </Card>

          <Card size="small" title="Invoices" extra={
            <Button size="small" onClick={() => act(() => api.generateInvoice(tenantId!), 'Invoice generated')}>Generate invoice</Button>
          }>
            <Table
              rowKey="id" size="small" pagination={false} dataSource={data?.invoices || []}
              columns={[
                { title: 'Number', dataIndex: 'invoiceNumber' },
                { title: 'Total', dataIndex: 'totalAmount', render: (v: string, r: api.Invoice) => money(v, r.currency) },
                { title: 'Balance', dataIndex: 'balanceDue', render: (v: string, r: api.Invoice) => money(v, r.currency) },
                { title: 'Status', dataIndex: 'status', render: (s: string) => <Tag color={invColor[s]}>{s}</Tag> },
                {
                  title: '', key: 'a', render: (_: any, r: api.Invoice) => (
                    <Space>
                      <a onClick={() => downloadPdf(r.id)}>PDF</a>
                      {['draft', 'issued'].includes(r.status) && <a onClick={() => act(() => api.sendInvoice(r.id), 'Sent')}>Send</a>}
                      {r.status !== 'paid' && r.status !== 'void' && <a onClick={() => act(() => api.voidInvoice(r.id), 'Voided')}>Void</a>}
                    </Space>
                  ),
                },
              ]}
            />
          </Card>

          <Space wrap>
            <Button onClick={() => setPayOpen(true)}>Record manual payment</Button>
            <Button onClick={() => setCreditOpen(true)}>Credit adjustment</Button>
            <Popconfirm title="Suspend this account?" onConfirm={() => act(() => api.suspendAccount(tenantId!), 'Suspended')}><Button danger>Suspend</Button></Popconfirm>
            <Button onClick={() => act(() => api.reactivateAccount(tenantId!), 'Reactivated')}>Reactivate</Button>
            <Popconfirm title="End contract? Future billing stops." onConfirm={() => act(() => api.endContract(tenantId!), 'Contract ended')}><Button>End contract</Button></Popconfirm>
            <Button onClick={() => act(() => api.waiveReinstatementFee(tenantId!), 'Fee waived')}>Waive reinstatement fee</Button>
          </Space>
        </Space>
      )}

      <Modal title="Record manual payment" open={payOpen} onOk={submitPay} onCancel={() => setPayOpen(false)} okText="Record">
        <Form form={payForm} layout="vertical">
          <Form.Item name="invoiceId" label="Invoice" rules={[{ required: true }]}>
            <Select options={(data?.invoices || []).filter((i) => !['paid', 'void'].includes(i.status)).map((i) => ({ value: i.id, label: `${i.invoiceNumber} — ${money(i.balanceDue, i.currency)} due` }))} />
          </Form.Item>
          <Form.Item name="amount" label="Amount" rules={[{ required: true }]}><InputNumber min={0} style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="method" label="Method" initialValue="bank"><Select options={[{ value: 'bank' }, { value: 'card' }, { value: 'manual' }]} /></Form.Item>
          <Form.Item name="externalReference" label="Reference"><Input placeholder="bank transfer ref" /></Form.Item>
          <Form.Item name="notes" label="Notes"><Input.TextArea rows={2} /></Form.Item>
        </Form>
      </Modal>

      <Modal title="Credit adjustment" open={creditOpen} onOk={submitCredit} onCancel={() => setCreditOpen(false)} okText="Apply">
        <Form form={creditForm} layout="vertical">
          <Form.Item name="amount" label={`Amount (${a?.currency || 'TZS'}) — positive = credit, negative = debit`} rules={[{ required: true }]}>
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="reason" label="Reason" rules={[{ required: true }]}>
            <Input.TextArea rows={2} placeholder="e.g. goodwill credit, negotiated discount" />
          </Form.Item>
        </Form>
      </Modal>
    </Drawer>
  );
};

const Tenants: React.FC = () => {
  const [rows, setRows] = useState<api.BillingAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState<string | null>(null);
  const load = () => { setLoading(true); api.getTenants().then(setRows).finally(() => setLoading(false)); };
  useEffect(load, []);
  return (
    <>
      <Button icon={<ReloadOutlined />} onClick={load} className="mb-3">Refresh</Button>
      <Table
        rowKey="tenantId" loading={loading} dataSource={rows} pagination={{ pageSize: 15 }}
        onRow={(r) => ({ onClick: () => setOpen(r.tenantId), style: { cursor: 'pointer' } })}
        columns={[
          { title: 'Tenant', dataIndex: 'tenantNameSnapshot', render: (v, r) => v || r.tenantId.slice(0, 8) },
          { title: 'Status', dataIndex: 'accountStatus', render: (s) => <Tag color={statusColor[s]}>{s}</Tag> },
          { title: 'Balance', dataIndex: 'currentBalance', render: (v, r) => money(v, r.currency) },
          { title: 'Currency', dataIndex: 'currency' },
          { title: 'Country', dataIndex: 'tenantCountry' },
        ]}
      />
      <TenantDrawer tenantId={open} onClose={() => { setOpen(null); load(); }} />
    </>
  );
};

const Reports: React.FC = () => {
  const [rev, setRev] = useState<any[]>([]);
  const [aging, setAging] = useState<any[]>([]);
  const [pay, setPay] = useState<any[]>([]);
  useEffect(() => {
    api.getRevenue().then(setRev).catch(() => undefined);
    api.getAging().then(setAging).catch(() => undefined);
    api.getPaymentsReport().then(setPay).catch(() => undefined);
  }, []);
  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Card size="small" title="Revenue by month">
        <Table rowKey="month" size="small" pagination={false} dataSource={rev} columns={[{ title: 'Month', dataIndex: 'month' }, { title: 'Amount', dataIndex: 'amount', render: (v) => money(v) }, { title: 'Payments', dataIndex: 'count' }]} />
      </Card>
      <Card size="small" title="Aging (overdue balance by days past due)">
        <Table rowKey="bucket" size="small" pagination={false} dataSource={aging} columns={[{ title: 'Bucket (days)', dataIndex: 'bucket' }, { title: 'Amount', dataIndex: 'amount', render: (v) => money(v) }, { title: 'Invoices', dataIndex: 'count' }]} />
      </Card>
      <Card size="small" title="Payments by provider">
        <Table rowKey={(r) => `${r.provider}-${r.status}`} size="small" pagination={false} dataSource={pay} columns={[{ title: 'Provider', dataIndex: 'provider' }, { title: 'Status', dataIndex: 'status' }, { title: 'Amount', dataIndex: 'amount', render: (v) => money(v) }, { title: 'Count', dataIndex: 'count' }]} />
      </Card>
    </Space>
  );
};

const SettingsTab: React.FC = () => {
  const [form] = Form.useForm();
  useEffect(() => { api.getSettings().then((s) => form.setFieldsValue(s)).catch(() => undefined); }, []);
  const save = async () => {
    const v = await form.validateFields();
    try { await api.updateSettings(v); message.success('Settings saved'); } catch (e: any) { message.error(e?.response?.data?.message || 'Save failed'); }
  };
  return (
    <Card title="Reinstatement & billing defaults" style={{ maxWidth: 640 }}>
      <Form form={form} layout="vertical">
        <Form.Item name="reinstatementFeeEnabled" label="Reinstatement fee enabled" valuePropName="checked"><Switch /></Form.Item>
        <Form.Item name="reinstatementFeeType" label="Fee type"><Select options={[{ value: 'fixed' }, { value: 'percentage' }]} /></Form.Item>
        <Form.Item name="reinstatementFeeAmount" label="Fixed amount"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item>
        <Form.Item name="reinstatementFeePercentage" label="Percentage of overdue"><InputNumber min={0} max={100} style={{ width: '100%' }} /></Form.Item>
        <Form.Item name="autoReactivateAfterPayment" label="Auto-reactivate after full payment" valuePropName="checked"><Switch /></Form.Item>
        <Form.Item name="allowFeeWaiver" label="Allow fee waiver" valuePropName="checked"><Switch /></Form.Item>
        <Form.Item name="defaultGracePeriodDays" label="Default grace period (days)"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item>
        <Form.Item name="defaultPaymentTermsDays" label="Default payment terms (days)"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item>
        <Button type="primary" onClick={save}>Save settings</Button>
      </Form>
    </Card>
  );
};

// --- Plans catalog (platform pricing) -------------------------------------
const PlansTab: React.FC = () => {
  const [plans, setPlans] = useState<api.Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<api.Plan | null>(null);
  const [open, setOpen] = useState(false);
  const [versionsFor, setVersionsFor] = useState<api.Plan | null>(null);
  const [form] = Form.useForm();

  const load = () => {
    setLoading(true);
    api.getPlans().then(setPlans).catch(() => message.error('Could not load plans')).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); form.resetFields(); form.setFieldsValue({ currency: 'TZS', isActive: true }); setOpen(true); };
  const openEdit = (p: api.Plan) => { setEditing(p); form.setFieldsValue({ ...p, features: (p.features || []).join(', ') }); setOpen(true); };
  const submit = async () => {
    try {
      const v = await form.validateFields();
      const body = { ...v, features: String(v.features || '').split(',').map((s) => s.trim()).filter(Boolean) };
      if (editing) await api.updatePlan(editing.id, body);
      else await api.createPlan(body);
      message.success('Saved'); setOpen(false); load();
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.message || 'Save failed');
    }
  };
  const toggle = async (p: api.Plan) => {
    try { await api.updatePlan(p.id, { isActive: !p.isActive }); load(); } catch { message.error('Failed'); }
  };

  return (
    <>
      <Space style={{ marginBottom: 12 }}>
        <Button type="primary" onClick={openCreate}>New plan</Button>
        <Button icon={<ReloadOutlined />} onClick={load} />
      </Space>
      <Table
        rowKey="id" loading={loading} dataSource={plans} pagination={false} size="small"
        columns={[
          { title: 'Code', dataIndex: 'code', render: (c: string) => <Text code>{c}</Text> },
          { title: 'Name', dataIndex: 'name' },
          { title: 'Monthly', dataIndex: 'defaultMonthlyAmount', align: 'right' as const, render: (v: string, r: api.Plan) => money(v, r.currency) },
          { title: 'Yearly', dataIndex: 'defaultYearlyAmount', align: 'right' as const, render: (v: string, r: api.Plan) => money(v, r.currency) },
          { title: 'Features', dataIndex: 'features', render: (f: string[]) => (f || []).map((x) => <Tag key={x}>{x}</Tag>) },
          { title: 'Status', dataIndex: 'isActive', render: (a: boolean) => <Tag color={a ? 'green' : 'default'}>{a ? 'active' : 'inactive'}</Tag> },
          {
            title: '', key: 'act', render: (_: any, r: api.Plan) => (
              <Space>
                <Button size="small" type="link" onClick={() => setVersionsFor(r)}>Versions</Button>
                <Button size="small" type="link" onClick={() => openEdit(r)}>Edit</Button>
                <Button size="small" type="link" onClick={() => toggle(r)}>{r.isActive ? 'Deactivate' : 'Activate'}</Button>
              </Space>
            ),
          },
        ]}
      />
      <Modal title={editing ? 'Edit plan' : 'New plan'} open={open} onOk={submit} onCancel={() => setOpen(false)} okText="Save">
        <Form form={form} layout="vertical">
          <Row gutter={12}>
            <Col span={12}><Form.Item name="code" label="Code" rules={[{ required: true }]}><Input placeholder="growth" disabled={!!editing} /></Form.Item></Col>
            <Col span={12}><Form.Item name="name" label="Name" rules={[{ required: true }]}><Input placeholder="Growth" /></Form.Item></Col>
          </Row>
          <Form.Item name="description" label="Description"><Input.TextArea rows={2} /></Form.Item>
          <Row gutter={12}>
            <Col span={8}><Form.Item name="defaultMonthlyAmount" label="Monthly" rules={[{ required: true }]}><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={8}><Form.Item name="defaultYearlyAmount" label="Yearly" rules={[{ required: true }]}><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={8}><Form.Item name="currency" label="Currency"><Input /></Form.Item></Col>
          </Row>
          <Form.Item name="features" label="Legacy feature codes (comma-separated — superseded by plan VERSIONS)"><Input placeholder="developer_platform, ..." /></Form.Item>
          <Form.Item name="isActive" label="Active" valuePropName="checked"><Switch /></Form.Item>
        </Form>
      </Modal>
      <PlanVersionsDrawer plan={versionsFor} onClose={() => setVersionsFor(null)} />
    </>
  );
};

// --- Renewals (proactive ops queue) ---------------------------------------
const RenewalsTab: React.FC = () => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState<api.Renewals | null>(null);
  const [loading, setLoading] = useState(true);
  const load = () => {
    setLoading(true);
    api.getRenewals(days).then(setData).catch(() => message.error('Could not load renewals')).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [days]);

  const dt = (d?: string) => (d ? fmtDate(d) : '—');

  return (
    <>
      <Space style={{ marginBottom: 12 }}>
        <Text type="secondary">Window</Text>
        <Select value={days} onChange={setDays} style={{ width: 150 }}
          options={[{ value: 30, label: 'Next 30 days' }, { value: 60, label: 'Next 60 days' }, { value: 90, label: 'Next 90 days' }]} />
        <Button icon={<ReloadOutlined />} onClick={load} />
      </Space>
      <Card size="small" title={`Upcoming invoices (${data?.upcomingInvoices.length ?? 0})`} style={{ marginBottom: 16 }}>
        <Table
          rowKey="id" loading={loading} size="small" pagination={false} dataSource={data?.upcomingInvoices || []}
          columns={[
            { title: 'Tenant', render: (_: any, r: api.UpcomingInvoice) => r.tenantName || r.tenantId },
            { title: 'Amount', dataIndex: 'amount', align: 'right' as const, render: (v: string, r: api.UpcomingInvoice) => money(v, r.currency) },
            { title: 'Cycle', dataIndex: 'billingCycle' },
            { title: 'Next invoice', dataIndex: 'nextInvoiceDate', render: (d: string) => dt(d) },
            { title: 'Status', dataIndex: 'status', render: (s: string) => <Tag color={statusColor[s] || 'default'}>{s}</Tag> },
            { title: 'Provider', dataIndex: 'defaultPaymentProvider' },
          ]}
        />
      </Card>
      <Card size="small" title={`Expiring contracts (${data?.expiringContracts.length ?? 0})`}>
        <Table
          rowKey="tenantId" loading={loading} size="small" pagination={false} dataSource={data?.expiringContracts || []}
          columns={[
            { title: 'Tenant', render: (_: any, r: api.ExpiringContract) => r.tenantNameSnapshot || r.tenantId },
            { title: 'Country', dataIndex: 'tenantCountry' },
            { title: 'Contract ends', dataIndex: 'contractEndDate', render: (d: string) => dt(d) },
            { title: 'Account', dataIndex: 'accountStatus', render: (s: string) => <Tag color={statusColor[s] || 'default'}>{s}</Tag> },
          ]}
        />
      </Card>
    </>
  );
};

export const BillingPage: React.FC = () => (
  <div className="p-1">
    <Space align="center" className="mb-4">
      <DollarOutlined style={{ fontSize: 22 }} />
      <Title level={3} style={{ margin: 0 }}>Billing</Title>
    </Space>
    <Tabs
      defaultActiveKey="overview"
      items={[
        { key: 'overview', label: 'Overview', children: <Overview /> },
        { key: 'tenants', label: 'Tenants', children: <Tenants /> },
        { key: 'plans', label: 'Plans', children: <PlansTab /> },
        { key: 'renewals', label: 'Renewals', children: <RenewalsTab /> },
        { key: 'reports', label: 'Reports', children: <Reports /> },
        { key: 'settings', label: 'Settings', children: <SettingsTab /> },
      ]}
    />
  </div>
);

export default BillingPage;
